
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useExistingStartupAccount } from "@/hooks/startup/useExistingStartupAccount";
import { useStartupApplicationForm } from "@/hooks/startup/useStartupApplicationForm";
import CategoryTagsSection from "./CategoryTagsSection";
import ApplicationLoadingState from "./ApplicationLoadingState";
import ExistingApplicationCard from "./ExistingApplicationCard";

interface StartupApplicationFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const StartupApplicationForm: React.FC<StartupApplicationFormProps> = ({ onSuccess, onCancel }) => {
  const { checkingExisting, hasExistingAccount, setHasExistingAccount } = useExistingStartupAccount();
  const {
    formData,
    loading,
    handleInputChange,
    addCategoryTag,
    removeCategoryTag,
    handleSubmit
  } = useStartupApplicationForm(onSuccess, setHasExistingAccount);

  if (checkingExisting) {
    return <ApplicationLoadingState />;
  }

  if (hasExistingAccount) {
    return <ExistingApplicationCard onCancel={onCancel} />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Apply to Teenvisor for Startups</CardTitle>
          <CardDescription>
            Tell us about your startup and how you plan to engage with teen users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => handleSubmit(e, hasExistingAccount)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="startup_name">Startup Name *</Label>
              <Input
                id="startup_name"
                value={formData.startup_name}
                onChange={(e) => handleInputChange("startup_name", e.target.value)}
                placeholder="Enter your startup name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">About Your Startup *</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => handleInputChange("bio", e.target.value)}
                placeholder="Describe your startup, mission, and how you plan to engage with teen users..."
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                type="url"
                value={formData.website_url}
                onChange={(e) => handleInputChange("website_url", e.target.value)}
                placeholder="https://your-startup.com"
              />
            </div>

            <CategoryTagsSection
              categoryTags={formData.category_tags}
              onAddTag={addCategoryTag}
              onRemoveTag={removeCategoryTag}
            />

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={loading || hasExistingAccount} className="flex-1">
                {loading ? "Submitting..." : "Submit Application"}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StartupApplicationForm;
